import { useState, type FormEvent } from "react";
import { PLAN_LABEL, planWith } from "../../shared/market";
import { BOUGHT_SET, SEGMENTS } from "../../shared/marketCustomers";
import { SHOP_EMAIL_BODY_MAX, SHOP_EMAIL_LISTINGS, SHOP_EMAIL_SUBJECT_MAX, type ShopEmailsView } from "../../shared/marketEmails";
import { ErrorMessage, Loading } from "../components/StatusMessage";
import { useI18n } from "../i18n/I18nProvider";
import type { MessageKey } from "../i18n/messages";
import { ApiError } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { marketApi, type AutomationView } from "./api";
import { useArs } from "./components";
import { ShowcaseOr } from "./Showcase";

const segmentKey = (segment: string): { key: MessageKey; set?: string } => (segment.startsWith(BOUGHT_SET) ? { key: "cus.seg.boughtSet", set: segment.slice(BOUGHT_SET.length) } : { key: `cus.seg.${segment}` as MessageKey });

/** Mi tienda › Emails (VMAX): write to a group of customers or followers; pokefans sends it on the shop's behalf. */
export function ShopEmailsTab({ allowed }: { allowed: boolean }) {
  const { t } = useI18n();
  const data = useAsync((signal) => (allowed ? marketApi.shopEmails(signal) : Promise.resolve(null)), [allowed]);
  const [view, setView] = useState<ShopEmailsView | null>(null);
  const v = view ?? data.data;
  if (!allowed) {
    return (
      <ShowcaseOr kind="emails">
        <section className="plan-card" aria-label={t("mkt.tab.emails")}>
          <p>{t("eml.teaser", { plan: PLAN_LABEL[planWith("customers")] })}</p>
        </section>
      </ShowcaseOr>
    );
  }
  if (data.error) return <ErrorMessage onRetry={data.reload} />;
  if (!v) return <Loading />;
  return (
    <div className="stack">
      <p className="muted small">{t("eml.intro")}</p>
      {v.paused && <p className="notice">{t("eml.paused")}</p>}
      {v.blocked !== null && <p className="notice notice-warn">{t("eml.blocked", { reason: v.blocked })}</p>}
      <p className="small">
        {t("eml.left", { left: v.left, max: v.perMonth })} <span className="muted">{t("eml.rules")}</span>
      </p>
      {v.blocked === null && v.left > 0 && <EmailForm first={!v.campaigns.some((c) => !c.awaitingApproval && c.status !== "rejected")} onSent={setView} />}
      <CampaignList view={v} />
      <Automations />
    </div>
  );
}

const AUTO_LABEL: Record<AutomationView["kind"], { title: MessageKey; hint: MessageKey }> = {
  thanks: { title: "auto.thanks", hint: "auto.thanksHint" },
  winback: { title: "auto.winback", hint: "auto.winbackHint" },
  news: { title: "auto.news", hint: "auto.newsHint" },
};

/** The shop's automatic emails: a switch each, with the thanks coupon and the come-back coupon. */
function Automations() {
  const { t } = useI18n();
  const list = useAsync((signal) => marketApi.automations(signal), []);
  const coupons = useAsync((signal) => marketApi.coupons(signal), []);
  const [saved, setSaved] = useState<AutomationView[] | null>(null);
  const [note, setNote] = useState<{ kind: string; text: string } | null>(null);
  const rows = saved ?? list.data;
  if (!rows) return null;
  // Come back reaches inactive customers: coupons for anybody, or for that group.
  const usable = (coupons.data ?? []).filter((c) => c.status === "active" && (["shop", "products", "set"].includes(c.scope) || (c.scope === "segment" && c.segment === "inactive")));
  const save = async (e: FormEvent<HTMLFormElement>, a: AutomationView) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const config: Record<string, unknown> =
      a.kind === "thanks" ? { kind: f.get("kind"), value: Number(f.get("value")), days: Number(f.get("days")) } : a.kind === "winback" ? { couponId: f.get("coupon") ? Number(f.get("coupon")) : null } : {};
    try {
      setSaved(await marketApi.saveAutomation(a.kind, f.get("enabled") === "on", config));
      setNote({ kind: a.kind, text: t("auto.saved") });
    } catch (err) {
      setNote({ kind: a.kind, text: err instanceof ApiError && err.code === "validation_error" ? t("cpn.err.validation") : t("error.generic") });
    }
  };
  return (
    <section className="stack-sm" aria-labelledby="auto-title">
      <h3 id="auto-title">{t("auto.title")}</h3>
      <p className="muted small">{t("auto.intro")}</p>
      {rows.map((a) => (
        <form key={a.kind} className="plan-card stack-sm" onSubmit={(e) => void save(e, a)} aria-label={t(AUTO_LABEL[a.kind].title)}>
          <label className="check">
            <input type="checkbox" name="enabled" defaultChecked={a.enabled} /> <strong>{t(AUTO_LABEL[a.kind].title)}</strong>
          </label>
          <p className="muted small">{t(AUTO_LABEL[a.kind].hint)}</p>
          {a.kind === "thanks" && (
            <div className="btn-row customers-filters">
              <label>
                {t("cpn.kind")}{" "}
                <select name="kind" defaultValue={a.config.kind ?? "percent"}>
                  <option value="percent">{t("cpn.percent")}</option>
                  <option value="amount">{t("cpn.amount")}</option>
                </select>
              </label>
              <label>
                {t("cpn.value")} <input name="value" type="number" min={1} required defaultValue={a.config.value ?? 10} className="qty-input" />
              </label>
              <label>
                {t("auto.days")} <input name="days" type="number" min={1} max={365} defaultValue={a.config.days ?? 30} className="qty-input" />
              </label>
            </div>
          )}
          {a.kind === "winback" && usable.length > 0 && (
            <label className="small">
              {t("eml.coupon")}{" "}
              <select name="coupon" defaultValue={a.config.couponId ?? ""}>
                <option value="">{t("eml.noCoupon")}</option>
                {usable.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.code}
                  </option>
                ))}
              </select>
            </label>
          )}
          {a.sent > 0 && <p className="small">{t("auto.results", { sent: a.sent, clicks: a.clicks, orders: a.orders })}</p>}
          <div className="btn-row customers-filters">
            <button type="submit" className="btn btn-outline btn-sm">
              {t("auto.save")}
            </button>
            {note?.kind === a.kind && <span className="small" role="status">{note.text}</span>}
          </div>
        </form>
      ))}
    </section>
  );
}

function CampaignList({ view }: { view: ShopEmailsView }) {
  const { t, locale } = useI18n();
  if (!view.campaigns.length) return <p className="muted">{t("eml.empty")}</p>;
  return (
    <ul className="coupon-list">
      {view.campaigns.map((c) => {
        const seg = segmentKey(c.segment);
        return (
          <li key={c.id} className="coupon-row">
            <div>
              <strong>{c.subject}</strong> <span className={c.status === "sent" ? "pill pill-paid" : c.status === "rejected" ? "pill pill-unpaid" : "pill"}>{t(`eml.status.${c.status}` as MessageKey)}</span>
              {c.awaitingApproval && <span className="muted small"> · {t("eml.awaiting")}</span>}
              <div className="muted small">
                {t(seg.key, { set: seg.set ?? "" })} · {new Date(c.createdAt).toLocaleDateString(locale === "es" ? "es-AR" : "en-US")}
                {c.couponCode && ` · ${t("mkt.coupon")} ${c.couponCode}`}
              </div>
              {c.status === "sent" && <div className="small">{c.sent === null ? t("eml.resultsFew") : t("eml.results", { sent: c.sent, clicks: c.clicks ?? 0, orders: c.orders ?? 0 })}</div>}
              {c.rejectReason && <div className="small form-error">{t("eml.rejected", { reason: c.rejectReason })}</div>}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function EmailForm({ first, onSent }: { first: boolean; onSent: (v: ShopEmailsView) => void }) {
  const { t } = useI18n();
  const ars = useArs();
  const [segment, setSegment] = useState<string>("all");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formKey, setFormKey] = useState(0);
  const listings = useAsync((signal) => marketApi.myListings(signal), []);
  const coupons = useAsync((signal) => marketApi.coupons(signal), []);
  const size = useAsync((signal) => marketApi.segmentCount(segment, signal), [segment]);
  const live = (listings.data ?? []).filter((l) => l.status === "active" && l.quantity > 0);
  const sets = [...new Set(live.map((l) => l.setName).filter((n): n is string => Boolean(n)))].sort();
  // Only coupons everyone in the group can use: for anybody, or for exactly this group.
  const usable = (coupons.data ?? []).filter((c) => c.status === "active" && (["shop", "products", "set"].includes(c.scope) || (c.scope === "segment" && c.segment === segment)));

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const picked = f.getAll("listing").map(Number).slice(0, SHOP_EMAIL_LISTINGS);
    setBusy(true);
    setError(null);
    try {
      onSent(await marketApi.sendShopEmail({ segment, filters: {}, subject: String(f.get("subject") ?? ""), body, couponId: f.get("coupon") ? Number(f.get("coupon")) : null, listingIds: picked }));
      setBody("");
      setFormKey((n) => n + 1);
    } catch (err) {
      const code = err instanceof ApiError ? err.code : "";
      const fields = ((err instanceof ApiError ? err.details : undefined) as { fields?: string[] } | undefined)?.fields ?? [];
      setError(fields.includes("links") ? t("eml.err.links") : code === "emails_quota" || code === "emails_blocked" ? t(`eml.err.${code}` as MessageKey) : code === "validation_error" ? t("eml.err.validation") : t("error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form key={formKey} className="plan-card stack-sm" onSubmit={submit} aria-labelledby="eml-new">
      <h3 id="eml-new">{t("eml.new")}</h3>
      <div className="field">
        <label htmlFor="eml-seg">{t("eml.segment")}</label>
        <select id="eml-seg" value={segment} onChange={(e) => setSegment(e.target.value)}>
          {SEGMENTS.map((s) => (
            <option key={s} value={s}>
              {t(`cus.seg.${s}` as MessageKey)}
            </option>
          ))}
          {sets.map((n) => (
            <option key={n} value={`${BOUGHT_SET}${n}`}>
              {t("cus.seg.boughtSet", { set: n })}
            </option>
          ))}
        </select>
        {size.data && <span className="field-hint">{size.data.count === null ? t("cus.groupSizeFew") : t("cus.groupSize", { count: size.data.count })}</span>}
      </div>
      <div className="field">
        <label htmlFor="eml-subject">{t("eml.subject")}</label>
        <input id="eml-subject" name="subject" required minLength={3} maxLength={SHOP_EMAIL_SUBJECT_MAX} />
      </div>
      <div className="field">
        <label htmlFor="eml-body">{t("eml.body")}</label>
        <textarea id="eml-body" rows={6} required maxLength={SHOP_EMAIL_BODY_MAX} value={body} onChange={(e) => setBody(e.target.value)} />
        <span className="field-hint">
          {t("eml.bodyHint")} {body.length}/{SHOP_EMAIL_BODY_MAX}
        </span>
      </div>
      {live.length > 0 && (
        <fieldset className="check-list">
          <legend>{t("eml.listings")}</legend>
          {live.slice(0, 60).map((l) => (
            <label key={l.id}>
              <input type="checkbox" name="listing" value={l.id} /> {l.name}
              {l.setName ? ` · ${l.setName}` : ""} · {ars(l.priceArs)}
            </label>
          ))}
        </fieldset>
      )}
      {usable.length > 0 && (
        <div className="field">
          <label htmlFor="eml-coupon">{t("eml.coupon")}</label>
          <select id="eml-coupon" name="coupon" defaultValue="">
            <option value="">{t("eml.noCoupon")}</option>
            {usable.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code}
              </option>
            ))}
          </select>
        </div>
      )}
      {first && <p className="muted small">{t("eml.firstNote")}</p>}
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <div>
        <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
          {t("eml.send")}
        </button>
      </div>
    </form>
  );
}
